import React, { useCallback, useEffect, useState } from 'react';
import {
  downloadInventoryValueCsv,
  fetchInventoryValueReport,
  type InventoryValueBucket,
  type InventoryValueReportResponse,
} from '../api/reports';
import { ReportGeneratedHeader } from '../components/reports/ReportGeneratedHeader';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';

const STATUS_FILTER_NOTE =
  'Status filter: in_stock, to_list, and listed. Listed copies are included. Sold, reserved, pending approval, and CK trade hold are excluded.';

const ETCHED_PRICE_NOTE =
  'Etched copies count as foil: their preferred value uses the foil variant sell price when that price is positive, and otherwise the etched variant sell price.';

const CHANNEL_LISTING_NOTE =
  'Listed on a channel means the matching variant has a synced channel listing with quantity greater than zero. Pending, failed, and zero-quantity listings are not listed. This is separate from copy status listed.';

function money(value: number): string {
  return `$${value.toFixed(2)}`;
}

function statusLabel(status: string): string {
  if (status === 'in_stock') return 'In stock (in_stock)';
  if (status === 'to_list') return 'To list (to_list)';
  if (status === 'listed') return 'Listed (listed)';
  return status;
}

function listingLabel(listed: boolean): string {
  return listed ? 'Listed on a channel' : 'Not listed on a channel';
}

export const ReportsInventoryValuePage: React.FC = () => {
  const [report, setReport] = useState<InventoryValueReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchInventoryValueReport();
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inventory value report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadInventoryValueCsv();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Inventory value</h1>
          <p style={styles.subtitle}>
            {report?.status_filter_note ?? STATUS_FILTER_NOTE}
          </p>
          <p style={styles.note}>{report?.etched_price_note ?? ETCHED_PRICE_NOTE}</p>
          <p style={styles.note}>{report?.channel_listing_note ?? CHANNEL_LISTING_NOTE}</p>
        </div>
        <div style={styles.headerLinks}>
          <button
            type="button"
            onClick={() => {
              void handleExportCsv();
            }}
            disabled={isExporting || isLoading}
            style={styles.linkButton}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      <ReportGeneratedHeader
        generatedAt={report?.generated_at}
        onRefresh={() => {
          void loadReport();
        }}
        refreshing={isLoading}
      />

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <section style={styles.summaryGrid} aria-label="Inventory value totals">
            <SummaryCard label="Copies" value={String(report.totals.copy_count)} />
            <SummaryCard label="Cost" value={money(report.totals.cost)} />
            <SummaryCard label="Preferred value" value={money(report.totals.preferred_value)} />
            <SummaryCard label="Market value" value={money(report.totals.market_value)} />
          </section>
          <p style={styles.note}>
            Cost is copies.acquisition_cost. Preferred value uses the stored sell-price
            ladder. Market value uses products.tcg_market_price when it is set, otherwise
            products.market_price. Unpriced copies stay in the copy count and out of that
            value total.
            {report.totals.preferred_unpriced_count > 0
              ? ` ${report.totals.preferred_unpriced_count} copies have no preferred price.`
              : ''}
            {report.totals.market_unpriced_count > 0
              ? ` ${report.totals.market_unpriced_count} copies have no market price.`
              : ''}
          </p>

          <ValueTable
            title="By status"
            rows={report.by_status.map((row) => ({
              key: row.status,
              label: statusLabel(row.status),
              bucket: row,
            }))}
          />
          <ValueTable
            title="By channel listing"
            rows={report.by_channel_listing.map((row) => ({
              key: row.listed_on_channel ? 'listed' : 'not-listed',
              label: listingLabel(row.listed_on_channel),
              bucket: row,
            }))}
          />
        </>
      )}
    </div>
  );
};

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardLabel}>{label}</div>
      <div style={styles.cardValue}>{value}</div>
    </div>
  );
}

function ValueTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; label: string; bucket: InventoryValueBucket }>;
}) {
  return (
    <section style={styles.card}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{title}</th>
              <th style={styles.th}>Copies</th>
              <th style={styles.th}>Cost</th>
              <th style={styles.th}>Preferred value</th>
              <th style={styles.th}>Preferred unpriced</th>
              <th style={styles.th}>Market value</th>
              <th style={styles.th}>Market unpriced</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td style={styles.tdStrong}>{row.label}</td>
                <td style={styles.td}>{row.bucket.copy_count}</td>
                <td style={styles.td}>{money(row.bucket.cost)}</td>
                <td style={styles.td}>{money(row.bucket.preferred_value)}</td>
                <td style={styles.td}>{row.bucket.preferred_unpriced_count}</td>
                <td style={styles.td}>{money(row.bucket.market_value)}</td>
                <td style={styles.td}>{row.bucket.market_unpriced_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    maxWidth: '760px',
    color: colors.text,
    fontSize: typography.body.fontSize,
  },
  note: {
    marginTop: spacing.xs,
    marginBottom: 0,
    maxWidth: '760px',
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: spacing.md,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
  },
  cardLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  cardValue: {
    marginTop: spacing.xs,
    color: colors.text,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  },
  sectionTitle: {
    margin: 0,
    marginBottom: spacing.md,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '720px',
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  },
  tdStrong: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
  },
};
